import { useEffect, useMemo, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import PhoneMock from "@/components/PhoneMock";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { Printer, Smartphone } from "lucide-react";

function render(tpl, ctx) {
  if (!tpl) return "";
  const rendered = tpl.replace(/\{(\w+)\}/g, (_, k) => (ctx[k] !== undefined ? ctx[k] : `{${k}}`));
  // CTIA compliance: STOP disclaimer must be on its own line so it never
  // reads as part of a shortlink or CTA the customer is about to tap.
  const stop = "Reply STOP to opt out.";
  const trimmed = rendered.replace(/\s+Reply STOP to opt out\.\s*$/i, "").trimEnd();
  return `${trimmed}\n\n${stop}`;
}

function timeAgo(mins) {
  return new Date(Date.now() - mins * 60 * 1000).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
}

const MOCK_CTX_BASE = {
  first_name: "Alicia",
  full_name: "Alicia Bennett",
  vehicle: "2021 Toyota RAV4",
  services: "Brake Pads, Rotor Resurface",
  schedule_url: "https://drvlgc.co/s/8K3",
  call_url: "https://drvlgc.co/c/8K3",
  advisor_name: "Jayne",
};

export default function ExamplesPage() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => {});
  }, []);

  const ctx = useMemo(() => {
    if (!s) return null;
    return {
      ...MOCK_CTX_BASE,
      dealership: s.dealership_name || "Precision Auto Group",
      phone: s.dealership_phone || "(214) 555-0123",
      csi_manager_name: s.csi_manager_name || "Tom Tillman",
      csi_manager_phone: s.csi_manager_phone || "987-4378-8978",
      advisor_name: s.default_parts_advisor || MOCK_CTX_BASE.advisor_name,
    };
  }, [s]);

  if (!s || !ctx) return <DashboardShell><div className="text-sm text-muted-foreground">Loading…</div></DashboardShell>;

  const prefix = (s.coupon_prefix || "DD").toUpperCase();
  // Under the dealership name in the mockup, show the dealer's real phone
  // from Settings — never a placeholder like "Your dealership".
  const dealerFrom = s?.dealership_phone || "";

  const cards = [
    {
      key: "stage1",
      label: `Day ${s.stage_1_days || 7} · ${s.stage_1_discount || 30}% OFF`,
      accent: "bg-blue-600 text-white",
      sent: timeAgo(1),
      body: render(s.sms_template_stage_1, {
        ...ctx,
        discount: s.stage_1_discount || 30,
        coupon_code: `${prefix}-${s.stage_1_discount || 30}-A7B2K9`,
      }),
    },
    {
      key: "stage2",
      label: `Day ${s.stage_2_days || 15} · ${s.stage_2_discount || 40}% OFF`,
      accent: "bg-orange-500 text-white",
      sent: timeAgo(2),
      body: render(s.sms_template_stage_2, {
        ...ctx,
        discount: s.stage_2_discount || 40,
        coupon_code: `${prefix}-${s.stage_2_discount || 40}-A7B2K9`,
      }),
    },
    {
      key: "oil",
      label: "Free Oil Change",
      accent: "bg-sky-600 text-white",
      sent: timeAgo(3),
      body: render(s.sms_template_oil_change, {
        ...ctx,
        coupon_code: `${prefix}-OIL-A7B2K9`,
      }),
    },
    {
      key: "parts",
      label: "Parts Arrived",
      accent: "bg-amber-600 text-white",
      sent: timeAgo(4),
      body: render(s.sms_template_parts_arrived, {
        ...ctx,
        coupon_code: "",
      }),
    },
    {
      key: "csi",
      label: "CSI Follow-Up",
      accent: "bg-emerald-600 text-white",
      sent: timeAgo(5),
      body: render(s.sms_template_csi, ctx),
    },
  ];

  // Always show Body Shop mock on the printed sheet so the feature can be pitched
  // even when the toggle is off. (User request: sell the feature without turning it on globally.)
  cards.push({
    key: "body",
    label: `Body Shop · ${s.body_shop_discount || 10}% OFF`,
    accent: "bg-rose-600 text-white",
    sent: timeAgo(6),
    body: render(s.sms_template_body_shop, {
      ...ctx,
      discount: s.body_shop_discount || 10,
      coupon_code: `${prefix}-BODY-A7B2K9`,
    }),
  });

  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="Example Text Messages" />
      <div className="mb-6 flex items-start justify-between gap-4 no-print">
        <div>
          <div className="font-mono-label text-muted-foreground">Sales Collateral</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Example Sheet</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Every text your customers will actually receive, mocked up on a phone screen. Hand this
            to the dealer principal so they can see the tone, formatting, and how the coupon comes
            through — before signing anything.
          </p>
        </div>
        <Button onClick={() => window.print()} data-testid="print-examples-btn">
          <Printer className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Print sheet
        </Button>
      </div>

      <Card className="p-5 border-border mb-6 bg-navy text-white" data-testid="examples-hero">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-[hsl(var(--gold))] grid place-items-center shrink-0">
            <Smartphone className="w-5 h-5 text-neutral-900" strokeWidth={2} />
          </div>
          <div>
            <div className="font-mono-label text-[hsl(var(--gold-light))]">The pitch line</div>
            <div className="font-display text-xl font-bold tracking-tight mt-1">
              &quot;Here&apos;s exactly what {ctx.dealership} will send your customers. Real wording, real
              coupons, real link. No advisor lifts a finger.&quot;
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-8 justify-items-center">
        {cards.map((c) => (
          <div key={c.key} className="flex flex-col items-center" data-testid={`example-${c.key}`}>
            <PhoneMock
              dealer={ctx.dealership}
              from={dealerFrom}
              body={c.body}
              sent={c.sent}
              label={c.label}
              accent={c.accent}
              logoUrl={s?.dealership_logo_url}
              scheduleUrl={ctx.schedule_url}
              callUrl={c.key === "csi" ? undefined : ctx.call_url}
              testid={`phonemock-${c.key}`}
            />
            <div className="mt-3 text-center max-w-[280px]">
              <div className="font-mono-label text-muted-foreground text-[0.6rem]">
                What the dealer sees
              </div>
              <div className="text-xs text-foreground/80 mt-0.5 leading-snug">
                Blue link inside the text = auto-tappable on any phone. The two
                buttons below it are what a customer taps to book or call —{" "}
                <b>zero typing required</b>.
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 text-xs text-muted-foreground border-t border-border pt-4 max-w-3xl">
        Templates and dealership name auto-fill from Settings. Change the wording in Settings →
        SMS Templates and the examples update everywhere.
      </div>
    </DashboardShell>
  );
}
